// Record defaults the three shipped Create modals seed and the agent never proposes. Without them
// the chat editor opens with a blank Owner where Create Company shows the rep, and the platform's
// owner default hides it until a layout marks Owner required and Confirm locks.

import Color from "@mapmycustomers/shared/enum/Color";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { ChatCreatableEntityType } from "@mapmycustomers/shared/types/chat/ChatContent";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import User from "@mapmycustomers/shared/types/User";

import { getSelectableNewDealSeeds } from "@app/util/deal/dealLockPickers";

import { isCreateRecordValueEmpty } from "./createRecordSchema";

export interface CreateRecordSeedContext {
  currentUserId: User["id"];
  funnelStages: Record<Funnel["id"], Stage[]>;
  layout: FormLayout | undefined;
}

// Structural match for DealFormFields' own FunnelStageValue, declared here so a util does not
// import a component folder — see the Deal field model's circular-import hazard.
export interface CreateRecordFunnelStage {
  funnelId?: Funnel["id"];
  stageId?: Stage["id"];
}

// Mirrors CreateDealModal's funnelStageDefaultValues: a layout can default the stage without
// naming its funnel, so the funnel is recovered by finding the one holding that stage. Reads the
// PARENT schema, since a child layout is only selectable once a funnel and stage already exist.
export const resolveDealFunnelStageDefaults = (
  layout: FormLayout | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>
): CreateRecordFunnelStage => {
  const defaults: CreateRecordFunnelStage = {};
  layout?.schema.forEach((schemaField) => {
    if (schemaField.field === "funnelId") {
      defaults.funnelId = schemaField.defaultValue?.[0] as Funnel["id"] | undefined;
    } else if (schemaField.field === "dealStageId") {
      const defaultStageId = schemaField.defaultValue?.[0] as Stage["id"] | undefined;
      const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
        funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === defaultStageId)
      );
      if (defaultStageId !== undefined && funnelIdString) {
        defaults.funnelId = parseInt(funnelIdString);
        defaults.stageId = defaultStageId;
      }
    }
  });
  return defaults;
};

// A Person proposal can carry a single `name` the platform cannot write. Unlike CreatePersonModal's
// own split, a one-word name becomes the FIRST name here — its substring(0, indexOf(" "))
// yields an empty first name and the whole word as the last, blocking a required-firstName layout.
const personNameSeeds = (formValues: Record<string, unknown>): Record<string, unknown> => {
  const name = typeof formValues.name === "string" ? formValues.name.trim() : "";
  if (!name) {
    return {};
  }
  const boundary = name.indexOf(" ");
  return boundary === -1
    ? { firstName: name, lastName: "" }
    : { firstName: name.slice(0, boundary), lastName: name.slice(boundary + 1) };
};

const buildSeeds = (
  entityType: ChatCreatableEntityType,
  formValues: Record<string, unknown>,
  { currentUserId, funnelStages, layout }: CreateRecordSeedContext
): Record<string, unknown> => {
  // null rather than absent: it is what makes the Frequency Interval control show "None" chosen
  // instead of nothing chosen, exactly as the shipped Create modals do.
  const common = { cadenceInterval: null, user: currentUserId };
  switch (entityType) {
    case EntityType.COMPANY:
      return { ...common, color: Color.BLACK };
    case EntityType.DEAL:
      // No color: CreateDealModal does not seed one either.
      return {
        ...common,
        ...getSelectableNewDealSeeds(
          resolveDealFunnelStageDefaults(layout, funnelStages),
          funnelStages
        ),
      };
    case EntityType.PERSON:
      return { ...common, ...personNameSeeds(formValues), color: Color.BLACK };
    default: {
      const exhaustive: never = entityType;
      return exhaustive;
    }
  }
};

// Seeds fill only what the proposal left empty, so the agent always wins on any field it proposed.
const applyCreateRecordSeeds = (
  entityType: ChatCreatableEntityType,
  formValues: Record<string, unknown>,
  context: CreateRecordSeedContext
): Record<string, unknown> => {
  const seeds = buildSeeds(entityType, formValues, context);
  const result = { ...formValues };
  Object.entries(seeds).forEach(([key, value]) => {
    // A spread would not do: wireToFormValues emits an explicit undefined for every key the
    // proposal omits, and those would overwrite the seeds rather than defer to them.
    if (isCreateRecordValueEmpty(result[key])) {
      result[key] = value;
    }
  });
  return result;
};

export default applyCreateRecordSeeds;
